import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useUiLocale } from '../../app/ui-locale';
import type { InputType } from '../../models/form';
import {
  formatGregorianDate,
  formatJalaliDate,
  gregorianMonthLength,
  gregorianWeekdayIndex,
  jalaliMonthLength,
  jalaliWeekdayIndex,
  parseGregorianDate,
  parseJalaliDate,
  isValidJalaliDateTime,
  isValidPersianTime,
  todayGregorian,
  todayJalali,
  toGregorian,
  toJalali,
  type GregorianDateParts,
  type JalaliDateParts
} from '../../utils/persian-date';
import { toEnglishNumbers } from '../../utils/numbers';
import { publicCopy } from './public-copy';

export type PersianDateTimeKind = Extract<InputType, 'tdate' | 'ttime' | 'tdatetime'>;

type CalendarParts = { year: number; month: number; day: number };
type CalendarPanel = 'days' | 'months' | 'years';

interface Props {
  id: string;
  name: string;
  kind: PersianDateTimeKind;
  value: unknown;
  placeholder?: string;
  disabled?: boolean;
  tabIndex?: number;
  className?: string;
  style?: CSSProperties;
  attributes?: Record<string, string | boolean | number>;
  ariaInvalid?: boolean;
  describedBy?: string;
  addonLeft?: string;
  addonRight?: string;
  minDate?: string;
  maxDate?: string;
  onChange: (value: string) => void;
}

interface PopupPosition {
  left: number;
  top: number;
  width: number;
}

const jalaliMonthNames = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];
const jalaliWeekdayNames = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
const gregorianMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const gregorianWeekdayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const frenchMonthNames = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const frenchWeekdayNames = ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'];

function parseTime(raw: string): { hour: number; minute: number; second: number } {
  const match = /(?:^|\s)([01]?\d|2[0-3]):([0-5]?\d)(?::([0-5]?\d))?$/.exec(toEnglishNumbers(raw).trim());
  return match
    ? { hour: Number(match[1]), minute: Number(match[2]), second: Number(match[3] ?? 0) }
    : { hour: 0, minute: 0, second: 0 };
}

function isValidTime(raw: string): boolean {
  return /^(?:[01]?\d|2[0-3]):[0-5]?\d(?::[0-5]?\d)?$/.test(toEnglishNumbers(raw).trim());
}

function formatTime(hour: number, minute: number, second = 0): string {
  const base = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  return second ? `${base}:${String(second).padStart(2, '0')}` : base;
}

function datePart(value: string): string { return value.trim().split(/\s+/)[0] ?? ''; }
function timePart(value: string): string {
  const parts = value.trim().split(/\s+/);
  return parts.length > 1 ? parts.slice(1).join(' ') : value.includes(':') && !value.includes('/') ? value : '';
}

function sanitizeTypedValue(kind: PersianDateTimeKind, raw: string): string {
  const gregorian = toEnglishNumbers(raw);
  if (kind === 'tdate') return gregorian.replace(/[^0-9/\-]/g, '').slice(0, 10);
  if (kind === 'ttime') return gregorian.replace(/[^0-9:]/g, '').slice(0, 8);
  return gregorian.replace(/[^0-9/\-:\s]/g, '').replace(/\s+/g, ' ').slice(0, 19);
}

/** Normalizes valid manually typed canonical Jalali values while preserving invalid text for validation feedback. */
export function normalizePersianDateTimeValue(kind: PersianDateTimeKind, raw: unknown): string {
  const sanitized = sanitizeTypedValue(kind, String(raw ?? '')).trim();
  if (!sanitized) return '';
  if (kind === 'tdate') {
    const parsed = parseJalaliDate(sanitized);
    return parsed ? formatJalaliDate(parsed) : sanitized;
  }
  if (kind === 'ttime') {
    return isValidTime(sanitized) ? formatTime(parseTime(sanitized).hour, parseTime(sanitized).minute, parseTime(sanitized).second) : sanitized;
  }
  const date = parseJalaliDate(datePart(sanitized));
  const rawTime = timePart(sanitized);
  if (!date || !isValidTime(rawTime)) return sanitized;
  const parsedTime = parseTime(rawTime);
  return `${formatJalaliDate(date)} ${formatTime(parsedTime.hour, parsedTime.minute, parsedTime.second)}`;
}

export function isValidPersianDateTimeValue(kind: PersianDateTimeKind, raw: unknown): boolean {
  const value = toEnglishNumbers(raw).trim();
  if (kind === 'tdate') return Boolean(parseJalaliDate(value));
  if (kind === 'ttime') return isValidPersianTime(value);
  return isValidJalaliDateTime(value);
}

function clampJalali(parts: JalaliDateParts): JalaliDateParts {
  return { jy: Math.min(3177, Math.max(-60, parts.jy)), jm: Math.min(12, Math.max(1, parts.jm)), jd: 1 };
}

function clampCalendar(parts: CalendarParts, gregorian: boolean): CalendarParts {
  return gregorian
    ? { year: Math.min(9999, Math.max(1, parts.year)), month: Math.min(12, Math.max(1, parts.month)), day: 1 }
    : { year: Math.min(3177, Math.max(-60, parts.year)), month: Math.min(12, Math.max(1, parts.month)), day: 1 };
}

function jalaliToCalendar(parts: JalaliDateParts, gregorian: boolean): CalendarParts {
  if (!gregorian) return { year: parts.jy, month: parts.jm, day: parts.jd };
  const converted = toGregorian(parts.jy, parts.jm, parts.jd);
  return { year: converted.gy, month: converted.gm, day: converted.gd };
}

function calendarToJalali(parts: CalendarParts, gregorian: boolean): JalaliDateParts {
  if (!gregorian) return { jy: parts.year, jm: parts.month, jd: parts.day };
  return toJalali(parts.year, parts.month, parts.day);
}

function calendarToday(gregorian: boolean): CalendarParts {
  if (gregorian) {
    const today = todayGregorian();
    return { year: today.gy, month: today.gm, day: today.gd };
  }
  const today = todayJalali();
  return { year: today.jy, month: today.jm, day: today.jd };
}

function formatCalendarDate(parts: CalendarParts, gregorian: boolean): string {
  return gregorian
    ? formatGregorianDate({ gy: parts.year, gm: parts.month, gd: parts.day } as GregorianDateParts)
    : formatJalaliDate({ jy: parts.year, jm: parts.month, jd: parts.day });
}

function displayValueFromCanonical(kind: PersianDateTimeKind, canonical: string, gregorian: boolean): string {
  if (!canonical || kind === 'ttime' || !gregorian) return canonical;
  const rawDate = datePart(canonical);
  const year = Number(toEnglishNumbers(rawDate).split('/')[0] ?? 0);
  if (year >= 1700) return canonical;
  const parsed = parseJalaliDate(rawDate);
  if (!parsed) return canonical;
  const convertedGregorian = toGregorian(parsed.jy, parsed.jm, parsed.jd);
  const displayDate = formatGregorianDate(convertedGregorian);
  return kind === 'tdatetime' && timePart(canonical) ? `${displayDate} ${timePart(canonical)}` : displayDate;
}

function canonicalFromDisplay(kind: PersianDateTimeKind, display: string, gregorian: boolean): string {
  const sanitized = sanitizeTypedValue(kind, display).trim();
  if (!sanitized || !gregorian) return normalizePersianDateTimeValue(kind, sanitized);
  if (kind === 'ttime') return isValidTime(sanitized) ? formatTime(parseTime(sanitized).hour, parseTime(sanitized).minute, parseTime(sanitized).second) : sanitized;
  const parsedGregorian = parseGregorianDate(datePart(sanitized));
  if (!parsedGregorian) return sanitized;
  const jalali = toJalali(parsedGregorian.gy, parsedGregorian.gm, parsedGregorian.gd);
  const canonicalDate = formatJalaliDate(jalali);
  if (kind === 'tdate') return canonicalDate;
  const rawTime = timePart(sanitized);
  if (!isValidTime(rawTime)) return sanitized;
  const parsedTime = parseTime(rawTime);
  return `${canonicalDate} ${formatTime(parsedTime.hour, parsedTime.minute, parsedTime.second)}`;
}

function canonicalOrder(parts: JalaliDateParts): number {
  return parts.jy * 372 + (parts.jm - 1) * 31 + parts.jd;
}

export function PersianDateTimeInput({
  id,
  name,
  kind,
  value,
  placeholder,
  disabled,
  tabIndex,
  className,
  style,
  attributes,
  ariaInvalid,
  describedBy,
  addonLeft,
  addonRight,
  minDate,
  maxDate,
  onChange
}: Props) {
  const { locale } = useUiLocale();
  const copy = publicCopy(locale);
  const gregorian = locale !== 'fa';
  const canonicalText = String(value ?? '');
  const parsedDate = parseJalaliDate(datePart(canonicalText));
  const parsedMinDate = parseJalaliDate(minDate);
  const parsedMaxDate = parseJalaliDate(maxDate);
  const canonicalToday = useMemo(() => todayJalali(), []);
  const today = useMemo(() => calendarToday(gregorian), [gregorian]);
  const initialCalendar = jalaliToCalendar(parsedDate ?? parsedMinDate ?? parsedMaxDate ?? canonicalToday, gregorian);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => displayValueFromCanonical(kind, canonicalText, gregorian));
  const [view, setView] = useState<CalendarParts>(() => clampCalendar(initialCalendar, gregorian));
  const [calendarPanel, setCalendarPanel] = useState<CalendarPanel>('days');
  const [popupPosition, setPopupPosition] = useState<PopupPosition>({ left: 8, top: 8, width: 320 });
  const rootRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const includesDate = kind !== 'ttime';
  const includesTime = kind !== 'tdate';
  const time = parseTime(timePart(canonicalText));
  const labels = {
    dialog: copy.selectDateTime,
    previousMonth: copy.previousMonth,
    nextMonth: copy.nextMonth,
    hour: copy.hour,
    minute: copy.minute,
    second: copy.second,
    today: copy.today,
    now: copy.now,
    clear: copy.clear,
    confirm: copy.confirm,
    chooseTime: copy.selectTime,
    chooseDate: gregorian ? copy.selectGregorianDate : copy.selectJalaliDate,
    chooseDateTime: gregorian ? copy.selectGregorianDateTime : copy.selectJalaliDateTime
  };

  useEffect(() => {
    if (!editing) setDraft(displayValueFromCanonical(kind, canonicalText, gregorian));
  }, [canonicalText, editing, gregorian, kind]);

  useEffect(() => {
    const next = jalaliToCalendar(parsedDate ?? parsedMinDate ?? parsedMaxDate ?? canonicalToday, gregorian);
    setView(clampCalendar(next, gregorian));
  }, [canonicalToday.jd, canonicalToday.jm, canonicalToday.jy, gregorian, parsedDate?.jd, parsedDate?.jm, parsedDate?.jy, parsedMaxDate?.jd, parsedMaxDate?.jm, parsedMaxDate?.jy, parsedMinDate?.jd, parsedMinDate?.jm, parsedMinDate?.jy]);

  const positionPopup = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor || typeof window === 'undefined') return;
    const rect = anchor.getBoundingClientRect();
    const viewportPadding = 8;
    const width = Math.max(240, Math.min(320, window.innerWidth - viewportPadding * 2));
    const measuredHeight = popupRef.current?.offsetHeight ?? (includesDate && includesTime ? 430 : includesDate ? 365 : 190);
    let left = gregorian ? rect.left : rect.right - width;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - width - viewportPadding));
    let top = rect.bottom + 4;
    if (top + measuredHeight > window.innerHeight - viewportPadding) {
      const above = rect.top - measuredHeight - 4;
      top = above >= viewportPadding ? above : Math.max(viewportPadding, window.innerHeight - measuredHeight - viewportPadding);
    }
    setPopupPosition({ left, top, width });
  }, [gregorian, includesDate, includesTime]);

  useLayoutEffect(() => {
    if (!open) return;
    positionPopup();
    const frame = window.requestAnimationFrame(positionPopup);
    return () => window.cancelAnimationFrame(frame);
  }, [open, view.year, view.month, kind, positionPopup]);

  useEffect(() => {
    if (open) setCalendarPanel('days');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !popupRef.current?.contains(target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const reposition = () => positionPopup();
    document.addEventListener('mousedown', close, true);
    document.addEventListener('keydown', escape, true);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', close, true);
      document.removeEventListener('keydown', escape, true);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, positionPopup]);

  const isDateAllowed = (parts: CalendarParts): boolean => {
    const canonical = calendarToJalali(parts, gregorian);
    const order = canonicalOrder(canonical);
    return (!parsedMinDate || order >= canonicalOrder(parsedMinDate)) && (!parsedMaxDate || order <= canonicalOrder(parsedMaxDate));
  };

  const setDate = (parts: CalendarParts) => {
    if (!isDateAllowed(parts)) return;
    const canonical = formatJalaliDate(calendarToJalali(parts, gregorian));
    const next = kind === 'tdate' ? canonical : `${canonical} ${formatTime(time.hour, time.minute, time.second)}`;
    onChange(next);
    setDraft(displayValueFromCanonical(kind, next, gregorian));
  };

  const setTime = (next: Partial<typeof time>) => {
    const merged = { ...time, ...next };
    const formatted = formatTime(merged.hour, merged.minute, merged.second);
    const canonicalDate = formatJalaliDate(parsedDate ?? parsedMinDate ?? parsedMaxDate ?? canonicalToday);
    const result = kind === 'ttime' ? formatted : `${canonicalDate} ${formatted}`;
    onChange(result);
    setDraft(displayValueFromCanonical(kind, result, gregorian));
  };

  const moveMonth = (delta: number) => {
    setView((current) => {
      const absolute = current.year * 12 + current.month - 1 + delta;
      const year = Math.floor(absolute / 12);
      const month = ((absolute % 12) + 12) % 12 + 1;
      return clampCalendar({ year, month, day: 1 }, gregorian);
    });
  };

  const moveCalendarPanel = (delta: number) => {
    if (calendarPanel === 'days') { moveMonth(delta); return; }
    setView((current) => clampCalendar({
      year: current.year + delta * (calendarPanel === 'years' ? 12 : 1),
      month: current.month,
      day: 1
    }, gregorian));
  };

  const yearPageStart = Math.floor(view.year / 12) * 12;
  const yearOptions = Array.from({ length: 12 }, (_, index) => yearPageStart + index);

  const days = includesDate ? (gregorian ? gregorianMonthLength(view.year, view.month) : jalaliMonthLength(view.year, view.month)) : 0;
  const offset = includesDate ? (gregorian ? gregorianWeekdayIndex(view.year, view.month, 1) : jalaliWeekdayIndex(view.year, view.month, 1)) : 0;
  const cells = includesDate ? Array.from({ length: offset + days }, (_, index) => index < offset ? 0 : index - offset + 1) : [];
  const monthNames = locale === 'fr' ? frenchMonthNames : gregorian ? gregorianMonthNames : jalaliMonthNames;
  const weekdayNames = locale === 'fr' ? frenchWeekdayNames : gregorian ? gregorianWeekdayNames : jalaliWeekdayNames;
  const selectedCalendar = parsedDate ? jalaliToCalendar(parsedDate, gregorian) : undefined;
  const portalTarget = open ? rootRef.current?.closest('[data-dynamicforms-react-root]') : null;
  const inputPlaceholder = gregorian
    ? (kind === 'tdate' ? '2026/01/01' : kind === 'tdatetime' ? '2026/01/01 12:30' : '12:30')
    : placeholder;

  const popup = open ? <div
    ref={popupRef}
    id={`${id}-picker`}
    className={`df-persian-datetime__popup ${gregorian ? 'is-gregorian' : 'is-jalali'}`}
    style={{ left: popupPosition.left, top: popupPosition.top, width: popupPosition.width }}
    role="dialog"
    aria-label={labels.dialog}
    dir={gregorian ? 'ltr' : 'rtl'}
  >
    {includesDate ? <>
      <div className="df-persian-calendar__header">
        <button type="button" className="btn btn-xs btn-default" onClick={() => moveCalendarPanel(-1)} aria-label={labels.previousMonth}>‹</button>
        <div className="df-persian-calendar__selectors">
          <button type="button" className={calendarPanel === 'months' ? 'is-active' : ''} onClick={() => setCalendarPanel((current) => current === 'months' ? 'days' : 'months')}>{monthNames[view.month - 1]}</button>
          <button type="button" className={calendarPanel === 'years' ? 'is-active' : ''} onClick={() => setCalendarPanel((current) => current === 'years' ? 'days' : 'years')}>{view.year}</button>
        </div>
        <button type="button" className="btn btn-xs btn-default" onClick={() => moveCalendarPanel(1)} aria-label={labels.nextMonth}>›</button>
      </div>
      {calendarPanel === 'months' ? <div className="df-persian-calendar__picker-grid df-persian-calendar__months" role="listbox" aria-label={copy.selectMonth}>
        {monthNames.map((label, index) => <button type="button" key={label} className={view.month === index + 1 ? 'is-selected' : ''} onClick={() => { setView((current) => ({ ...current, month: index + 1, day: 1 })); setCalendarPanel('days'); }}>{label}</button>)}
      </div> : calendarPanel === 'years' ? <div className="df-persian-calendar__picker-grid df-persian-calendar__years" role="listbox" aria-label={copy.selectYear}>
        {yearOptions.map((year) => <button type="button" key={year} className={view.year === year ? 'is-selected' : ''} onClick={() => { setView((current) => clampCalendar({ ...current, year, day: 1 }, gregorian)); setCalendarPanel('days'); }}>{year}</button>)}
      </div> : <div className="df-persian-calendar" role="grid">
        {weekdayNames.map((label) => <span className="df-persian-calendar__weekday" key={label}>{label}</span>)}
        {cells.map((day, index) => day === 0
          ? <span className="df-persian-calendar__empty" key={`empty-${index}`} />
          : (() => {
              const parts = { year: view.year, month: view.month, day };
              const allowed = isDateAllowed(parts);
              const selected = selectedCalendar?.year === view.year && selectedCalendar.month === view.month && selectedCalendar.day === day;
              return <button
                type="button"
                key={day}
                className={`${selected ? 'is-selected' : ''}${allowed ? '' : ' is-disabled'}`.trim()}
                disabled={!allowed}
                aria-label={`${day} ${monthNames[view.month - 1]} ${view.year}`}
                onClick={() => setDate(parts)}
              >{day}</button>;
            })())}
      </div>}
    </> : null}

    {includesTime ? <div className="df-time-picker" dir="ltr" aria-label={labels.chooseTime}>
      <label dir={gregorian ? 'ltr' : 'rtl'}><span>{labels.hour}</span><select className="form-control" value={time.hour} onChange={(event) => setTime({ hour: Number(event.target.value) })}>{Array.from({ length: 24 }, (_, hour) => <option key={hour} value={hour}>{String(hour).padStart(2, '0')}</option>)}</select></label>
      <span aria-hidden="true">:</span>
      <label dir={gregorian ? 'ltr' : 'rtl'}><span>{labels.minute}</span><select className="form-control" value={time.minute} onChange={(event) => setTime({ minute: Number(event.target.value) })}>{Array.from({ length: 60 }, (_, minute) => <option key={minute} value={minute}>{String(minute).padStart(2, '0')}</option>)}</select></label>
      <span aria-hidden="true">:</span>
      <label dir={gregorian ? 'ltr' : 'rtl'}><span>{labels.second}</span><select className="form-control" value={time.second} onChange={(event) => setTime({ second: Number(event.target.value) })}>{Array.from({ length: 60 }, (_, second) => <option key={second} value={second}>{String(second).padStart(2, '0')}</option>)}</select></label>
    </div> : null}

    <div className="df-persian-datetime__actions">
      {includesDate ? <button type="button" className="btn btn-xs btn-default" disabled={!isDateAllowed(today)} onClick={() => { setView(clampCalendar(today, gregorian)); setDate(today); }}>{labels.today}</button> : <button type="button" className="btn btn-xs btn-default" onClick={() => { const now = new Date(); setTime({ hour: now.getHours(), minute: now.getMinutes(), second: 0 }); }}>{labels.now}</button>}
      <button type="button" className="btn btn-xs btn-default" onClick={() => { onChange(''); setDraft(''); }}>{labels.clear}</button>
      <button type="button" className="btn btn-xs btn-primary" onClick={() => setOpen(false)}>{labels.confirm}</button>
    </div>
  </div> : null;

  return <div ref={rootRef} className="df-persian-datetime">
    <div ref={anchorRef} className="input-group df-input-group df-persian-datetime__input-group">
      {addonLeft ? <span className="input-group-addon input-group-text">{addonLeft}</span> : null}
      <input
        {...attributes}
        id={id}
        name={name}
        className={className}
        style={style}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={editing ? draft : displayValueFromCanonical(kind, canonicalText, gregorian)}
        placeholder={inputPlaceholder}
        disabled={disabled}
        tabIndex={tabIndex}
        aria-invalid={ariaInvalid}
        aria-describedby={describedBy}
        aria-haspopup="dialog"
        aria-expanded={open}
        onFocus={() => { setEditing(true); setDraft(displayValueFromCanonical(kind, canonicalText, gregorian)); }}
        onChange={(event) => {
          const next = sanitizeTypedValue(kind, event.target.value);
          setDraft(next);
          if (!gregorian || kind === 'ttime') onChange(next);
        }}
        onBlur={() => {
          const next = canonicalFromDisplay(kind, draft, gregorian);
          onChange(next);
          setDraft(displayValueFromCanonical(kind, next, gregorian));
          setEditing(false);
        }}
      />
      <button
        type="button"
        className="btn btn-default df-persian-datetime__toggle"
        disabled={disabled}
        aria-label={kind === 'ttime' ? labels.chooseTime : kind === 'tdate' ? labels.chooseDate : labels.chooseDateTime}
        aria-controls={`${id}-picker`}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >{kind === 'ttime' ? '◷' : '▦'}</button>
      {addonRight ? <span className="input-group-addon input-group-text">{addonRight}</span> : null}
    </div>
    {portalTarget ? createPortal(popup, portalTarget) : popup}
  </div>;
}
